import React, { useState, useEffect } from 'react'
import { Calendar, Clock, X, ChevronLeft, ChevronRight, Loader } from 'lucide-react'

interface CalendarPopupProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (date: string, time: string) => void
  selectedDate?: string
  selectedTime?: string
}

const CalendarPopup: React.FC<CalendarPopupProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedDate = '',
  selectedTime = ''
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDateState, setSelectedDateState] = useState(selectedDate)
  const [availableSlots, setAvailableSlots] = useState<string[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedTimeState, setSelectedTimeState] = useState(selectedTime)

  // Get today's date for minimum date validation
  const today = new Date()
  const todayString = today.toISOString().split('T')[0]

  // Generate calendar days (fixed timezone issues)
  const generateCalendarDays = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()

    // Create dates in local timezone to avoid UTC shifts
    const firstDay = new Date(year, month, 1)
    const startOfWeek = firstDay.getDay() // 0 = Sunday, 1 = Monday, etc.

    const days = []

    // Start from the Sunday before the first day of the month
    for (let i = 0; i < 42; i++) { // 6 weeks
      const dayOffset = i - startOfWeek
      const day = new Date(year, month, 1 + dayOffset)
      days.push(day)
    }

    return days
  }

  const days = generateCalendarDays(currentMonth)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  // Handle date selection
  const handleDateSelect = async (date: Date) => {
    // Fix timezone issue - use local date instead of UTC
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}`

    // Don't allow past dates
    if (dateString < todayString) return

    setSelectedDateState(dateString)
    setSelectedTimeState('')
    setLoadingSlots(true)

    try {
      const response = await fetch(`/api/calendar/slots?date=${dateString}&duration=30`)
      const data = await response.json()
      if (data?.slots?.length) {
        setAvailableSlots(data.slots)
      } else {
        setAvailableSlots([])
      }
    } catch (error) {
      console.log('Failed to fetch slots:', error)
      // Fallback to business hours if API fails
      const defaultSlots = ['10:30', '11:00', '11:30', '12:00', '12:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30']
      setAvailableSlots(defaultSlots)
    }
    setLoadingSlots(false)
  }

  // Handle time selection
  const handleTimeSelect = (time: string) => {
    setSelectedTimeState(time)
  }

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedDateState && selectedTimeState) {
      onSelect(selectedDateState, selectedTimeState)
      onClose()
    }
  }

  // Format date for display
  const formatDateForDisplay = (dateString: string) => {
    const date = new Date(dateString + 'T12:00:00')
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedDateState(selectedDate)
      setSelectedTimeState(selectedTime)
      setAvailableSlots([])

      // If there's already a selected date, fetch its slots
      if (selectedDate) {
        handleDateSelect(new Date(selectedDate + 'T12:00:00'))
      }
    }
  }, [isOpen, selectedDate, selectedTime])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#F2F2F2] dark:bg-gray-800 rounded-3xl shadow-[20px_20px_40px_#dadada,-20px_-20px_40px_#ffffff] dark:shadow-[20px_20px_40px_#1f2937,-20px_-20px_40px_#374151] max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200/30 dark:border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-[#B89E6A]/20 to-[#D4B896]/30 rounded-xl flex items-center justify-center mr-3">
                <Calendar className="w-5 h-5 text-[#B89E6A]" />
              </div>
              <h3 className="text-xl font-light text-gray-800 dark:text-gray-200">
                Select <span className="text-[#B89E6A] font-normal">Date & Time</span>
              </h3>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-gray-200/50 dark:bg-gray-700/50 rounded-lg flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Calendar */}
          <div className="space-y-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={goToPreviousMonth}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 shadow-[inset_2px_2px_4px_#dadada,inset_-2px_-2px_4px_#ffffff] dark:shadow-[inset_2px_2px_4px_#1f2937,inset_-2px_-2px_4px_#374151] hover:shadow-[inset_4px_4px_8px_#dadada,inset_-4px_-4px_8px_#ffffff] dark:hover:shadow-[inset_4px_4px_8px_#1f2937,inset_-4px_-4px_8px_#374151] transition-all duration-200"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>

              <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h4>

              <button
                onClick={goToNextMonth}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700 shadow-[inset_2px_2px_4px_#dadada,inset_-2px_-2px_4px_#ffffff] dark:shadow-[inset_2px_2px_4px_#1f2937,inset_-2px_-2px_4px_#374151] hover:shadow-[inset_4px_4px_8px_#dadada,inset_-4px_-4px_8px_#ffffff] dark:hover:shadow-[inset_4px_4px_8px_#1f2937,inset_-4px_-4px_8px_#374151] transition-all duration-200"
              >
                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                // Fix timezone issue - create date string in local timezone
                const year = day.getFullYear()
                const month = String(day.getMonth() + 1).padStart(2, '0')
                const dayNum = String(day.getDate()).padStart(2, '0')
                const dateString = `${year}-${month}-${dayNum}`

                const isCurrentMonth = day.getMonth() === currentMonth.getMonth()
                const isPastDate = dateString < todayString
                const isSelected = dateString === selectedDateState
                const isToday = dateString === todayString

                return (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    disabled={isPastDate || !isCurrentMonth}
                    className={`
                      p-2 rounded-lg text-sm font-medium transition-all duration-200 relative
                      ${isSelected
                        ? 'bg-gradient-to-br from-[#B89E6A] to-[#D4B896] text-white shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2)]'
                        : isCurrentMonth && !isPastDate
                          ? 'bg-gray-100 dark:bg-gray-700 shadow-[2px_2px_4px_#dadada,-2px_-2px_4px_#ffffff] dark:shadow-[2px_2px_4px_#1f2937,-2px_-2px_4px_#374151] hover:shadow-[4px_4px_8px_#dadada,-4px_-4px_8px_#ffffff] dark:hover:shadow-[4px_4px_8px_#1f2937,-4px_-4px_8px_#374151] hover:scale-105 text-gray-800 dark:text-gray-200'
                          : 'text-gray-300 dark:text-gray-600'
                      }
                      ${isPastDate || !isCurrentMonth ? 'cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {day.getDate()}
                    {isToday && (
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#B89E6A] rounded-full"></div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected Date Display */}
          {selectedDateState && (
            <div className="p-4 bg-gradient-to-br from-[#B89E6A]/5 to-[#D4B896]/10 rounded-2xl border border-[#B89E6A]/20">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-[#B89E6A] mr-2" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {formatDateForDisplay(selectedDateState)}
                </span>
              </div>
            </div>
          )}

          {/* Time Slots */}
          {selectedDateState && (
            <div className="space-y-4">
              <div className="flex items-center">
                <Clock className="w-4 h-4 text-[#B89E6A] mr-2" />
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Available Times
                </h5>
              </div>

              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 text-[#B89E6A] animate-spin" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading available times...</span>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No available times for this date</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => handleTimeSelect(slot)}
                      className={`
                        p-3 rounded-xl text-sm font-medium transition-all duration-200
                        ${selectedTimeState === slot
                          ? 'bg-gradient-to-br from-[#B89E6A] to-[#D4B896] text-white shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2)]'
                          : 'bg-gray-100 dark:bg-gray-700 shadow-[2px_2px_4px_#dadada,-2px_-2px_4px_#ffffff] dark:shadow-[2px_2px_4px_#1f2937,-2px_-2px_4px_#374151] hover:shadow-[4px_4px_8px_#dadada,-4px_-4px_8px_#ffffff] dark:hover:shadow-[4px_4px_8px_#1f2937,-4px_-4px_8px_#374151] hover:scale-105 text-gray-800 dark:text-gray-200'
                        }
                      `}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200/30 dark:border-gray-700/30">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 shadow-[4px_4px_8px_#dadada,-4px_-4px_8px_#ffffff] dark:shadow-[4px_4px_8px_#1f2937,-4px_-4px_8px_#374151] hover:shadow-[inset_4px_4px_8px_#dadada,inset_-4px_-4px_8px_#ffffff] dark:hover:shadow-[inset_4px_4px_8px_#1f2937,inset_-4px_-4px_8px_#374151] text-gray-700 dark:text-gray-300 font-medium transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedDateState || !selectedTimeState}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-br from-[#B89E6A] to-[#D4B896] text-white shadow-[4px_4px_8px_#dadada,-4px_-4px_8px_#ffffff] dark:shadow-[4px_4px_8px_#1f2937,-4px_-4px_8px_#374151] hover:shadow-[6px_6px_12px_#dadada,-6px_-6px_12px_#ffffff] dark:hover:shadow-[6px_6px_12px_#1f2937,-6px_-6px_12px_#374151] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarPopup